import React, { useCallback, useEffect, useState } from "react";
import { Form, Button, FloatingLabel, CloseButton } from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import Logger from '../services/Logger';
import { LogElements, LogAction } from '../services/LogElements';
import {
    setUserRole,
    setUserGroups,
    setUserUniqueGroups,
    setUserOrganizations,
    setOrganizationsHierarchy,
    setTokens,
    setUserItem,
    setPermissions,
    setUser,
    setPreferences
} from '../slicers/UserSlice';
import { setDropdowns } from "../slicers/DropdownSlice";
import '../components/AccountManagement/Accounts.css';
import styles from '../views/account-management/AccountManagement.module.css';
import useAxios from "../services/AxiosConfig";
import inputStyles from './ManagementPages.module.css';
import { closeAlert, showAlert } from "../components/PerinHealthAlertSlice";
import { closeTimeOutAlert } from "../slicers/TimeOutAlertSlice";
import PerinHealthAlert from '../components/PerinHealthAlert';
import { addUnreadPatientMessageId, addUnreadStaffMessageId, clearUnreadPatientMessageIds, clearUnreadStaffMessageIds, setNewStaffMessageCount } from "../slicers/sidebarSlice";
import { refreshStaffTaskCount, refreshNewReportCount, refreshTodaysAppointmentsCount } from "../utils/sidebarUtils";
import { resetAppTourState, setFieldValue } from "./app-tour/AppTourSlice";
import { setSelectedMenuItem } from '../slicers/sidebarSlice';
import { showPasswordResetModal } from '../slicers/PasswordResetSlice';
import useVersion from '../hooks/useVersion';
import { getReleaseNotesUrl } from '../utils/versionUtils';
import { getEnvironmentLogoSrc } from '../utils/environmentLogos';

const MFA_MAX_FAILED_ATTEMPTS = 3;

function LoginBrandedTitle({ title, variant = 'row' }) {
    const logoSrc = getEnvironmentLogoSrc();

    if (variant === 'stacked') {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {logoSrc ? (
                    <img src={logoSrc} alt="" style={{ width: '35px', height: '35px' }} />
                ) : null}
                <h2 className="reg-title" style={{ margin: '30px 0 20px 0' }}>{title}</h2>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', margin: '30px 0 20px 0', width: '80%' }}>
            {logoSrc ? (
                <img src={logoSrc} alt="" style={{ width: '35px', height: '35px' }} />
            ) : null}
            <h2 className="reg-title" style={{ margin: '0' }}>{title}</h2>
        </div>
    );
}

const Login = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);

    const timeOutAlert = useSelector((state) => state.timeOutAlert);
    const perinHealthAlert = useSelector((state) => state.perinHealthAlert);
    
    // Get dynamic version
    const { version, refreshVersion } = useVersion();

    const [showPassword, setShowPassword] = useState(false);
    const [userLogin, setUserLogin] = useState({
        username: "",
        password: ""
    });
    const [mfaCode, setMfaCode] = useState('');
    const [session, setSession] = useState(null);
    const [challengeParameters, setChallengeParameters] = useState(null);
    const [mfaFailedAttempts, setMfaFailedAttempts] = useState(0);
    const [isMfaSubmitting, setIsMfaSubmitting] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [validated, setValidated] = useState(false);
    const [showPatientAlert, setShowPatientAlert] = useState(false);

    const checkRememberMe = useCallback(() => {
        try {
            const rememberMe = Boolean(JSON.parse(localStorage.getItem('rememberMe')));
            if (rememberMe) {
                const username = JSON.parse(localStorage.getItem('username'));
                setUserLogin(prevUserLogin => ({...prevUserLogin, "username": username}))
            }
            if (rememberMe !== null) {
                setRememberMe(rememberMe);
            }
        } catch (error) {
            console.error('Error getting stored email fo rmemerme:', error);
            return null;
        }
    }, []); // Remove userLogin from dependencies

    useEffect(()=> {
        checkRememberMe();
    },[checkRememberMe])

    const storeRememeberMe = () => {
        try {
            localStorage.setItem('rememberMe', JSON.stringify(rememberMe));
            if (rememberMe) {
                localStorage.setItem('username', JSON.stringify(userLogin.username));
            } else {
                localStorage.setItem('username', JSON.stringify(""));
            }
        } catch (error) {
            console.error('Error storing email for remember me:', error);
        }
    };

    const handleChange = (e) => {
        const { id, type, checked, value } = e.target;
        setUserLogin((prevUserLogin) => ({
            ...prevUserLogin,
            [id]: type === "checkbox" ? checked : value.trim(),
        }));
    };

    const handleLandingPage = (role) => {
        const landings = {
            "Super Admin": '/organization-management',
            "Admin": '/account-management',
            "Patient": '/patient-message',
        };
        const sideBarName = {
            "Super Admin": 'Hierarchy',
            "Admin": 'Accounts',
            "Patient": 'Patient Messages',
        };
        if (landings[role.category]) {
            dispatch(setSelectedMenuItem(sideBarName[role.category]));
            navigate(landings[role.category]);
        } else {
            dispatch(setSelectedMenuItem('Dashboard'));
            navigate('overview-dashboard');
        }
    };

    const handleUniqueOrganizations = (data) => {
        let uniqueOrgNames = new Set();
        let tempGroups = [];
        dispatch(setOrganizationsHierarchy(data));
        data.forEach(org => {
            uniqueOrgNames.add(org.organizationName);
            org.groups.forEach(group => {
                if (group.groupId !== null)
                    tempGroups.push({
                        name: group.groupName,
                        id: group.groupId,
                    });
            });
        });
        let tempOrgs = Array.from(uniqueOrgNames).map(orgName => ({
            name: orgName,
            id: data.find(org => org.organizationName === orgName).organizationId,
        }));

        dispatch(setUserOrganizations(tempOrgs));
        dispatch(setUserUniqueGroups(tempGroups));
    };

    const handleGroups = async (data, tokens) => {
        let groupsApi = data.role.name === "Super Admin" ? "admin/groups" : `list/groups/${data.id}`;
        axios.get(groupsApi, {
            headers: {
                authorization: `${tokens.authorization}`,
                refresh: `${tokens.refresh}`,
                id: `${tokens.id}`,
            }
        })
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    const groupData = responseData.data.data;
                    const hasInpatientGroup = groupData.some(org =>
                        org &&
                        org.groups &&
                        Array.isArray(org.groups) &&
                        org.groups.some(group =>
                            group.inpatient
                        )
                    );
                    dispatch(setUserItem({key: 'hasInpatientGroup', value: hasInpatientGroup}));
                    handleUniqueOrganizations(groupData);
                    dispatch(setUserGroups(groupData));
                } else {
                    console.error('Invalid data format:');
                }
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Log login button tap
        Logger.shared.info(
            LogElements.loginButton,
            LogAction.clicked,
            'Login.js'
        );
        
        const form = e.currentTarget;
        setIsLoading(true);

        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true); 
            setIsLoading(false); 
            return;             
        }

        setValidated(true);
        try {
            const response = await axios.request({
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                },
                url: 'signin',
                data: JSON.stringify(userLogin),
            });

            if (response.data.data.ChallengeName && response.data.data.Session) {
                setSession(response.data.data.Session);
                setChallengeParameters(response.data.data.ChallengeParameters ?? null);
                setMfaCode('');
                setMfaFailedAttempts(0);
            } else if (response.data.data.user) {
                storeRememeberMe()
                handleSuccessfulLogin(response.data.data);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message 
                || error.response?.error?.message
                || "Error signing in";
            
                    // Log failed login attempt
                    Logger.shared.debug(`Login failed: ${errorMessage}`, null, 'Login.js');
            
            dispatch(
                showAlert({
                    header: "Error",
                    message: errorMessage,
                    type: 'danger',
                })
            );
        }
        setIsLoading(false);
    };

    const handleResendMfaCode = async () => {
        setIsMfaSubmitting(true);
        try {
            const response = await axios.request({
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                },
                url: 'resend-mfa',
                data: JSON.stringify({
                    username: userLogin.username,
                    password: userLogin.password,
                }),
            });

            if (response.data.data?.Session) {
                setSession(response.data.data.Session);
                setChallengeParameters(response.data.data.ChallengeParameters ?? null);
                setMfaCode('');
                setMfaFailedAttempts(0);
                dispatch(showAlert({
                    header: "New code sent",
                    message: "A new verification code has been sent to your phone. Please enter it below.",
                    type: 'success',
                }));
            }
        } catch (error) {
            const resendMessage = error.response?.data?.message
                || "Failed to send a new verification code";
            dispatch(showAlert({
                header: "Error",
                message: resendMessage,
                type: 'danger',
            }));
        } finally {
            setIsMfaSubmitting(false);
        }
    };

    const handleMfaSubmit = async () => {
        setIsMfaSubmitting(true);
        try {
            const response = await axios.request({
                method: 'POST',
                headers: {
                    "Content-Type": "application/json"
                },
                url: 'verify-mfa',
                data: JSON.stringify({ username: userLogin.username, session, mfaCode }),
            });

            if (response.data.data.user) {
                setMfaFailedAttempts(0);
                handleSuccessfulLogin(response.data.data);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Invalid verification code";
            const nextAttempts = mfaFailedAttempts + 1;
            setMfaFailedAttempts(nextAttempts);

            if (nextAttempts >= MFA_MAX_FAILED_ATTEMPTS) {
                dispatch(showAlert({
                    header: "Error verifying MFA",
                    message: `${errorMessage} Click "Resend MFA Code" to receive a new verification code.`,
                    type: 'danger',
                }));
            } else {
                const attemptsRemaining = MFA_MAX_FAILED_ATTEMPTS - nextAttempts;
                dispatch(showAlert({
                    header: "Error verifying MFA",
                    message: `${errorMessage} ${attemptsRemaining} attempt${attemptsRemaining === 1 ? '' : 's'} remaining.`,
                    type: 'danger',
                }));
            }
        } finally {
            setIsMfaSubmitting(false);
        }
    };

    const handleSuccessfulLogin = async (data) => {
        if (data.user.role.category === "Patient") {
            // Log patient login attempt (blocked)
            Logger.shared.debug(`Login blocked: User ${data.user.id} attempted to access provider portal`, null, 'Login.js');
            setShowPatientAlert(true)
            return;
        }
        
        if (data.user.role.category === "Tester") {
            // Log tester redirect
            Logger.shared.debug(`Tester user ${data.user.id} redirected to test app`, null, 'Login.js');
            
            // Check if running on localhost
            const isLocalhost = window.location.hostname === 'localhost' || 
                               window.location.hostname === '127.0.0.1' ||
                               window.location.hostname.includes('localhost');
            
            // Set cookies with authentication tokens before redirecting
            if (data.tokens) {
                let cookieOptions;
                
                if (isLocalhost) {
                    // For localhost, don't set domain (or use 'localhost' without dot)
                    cookieOptions = {
                        path: '/',
                        secure: false, // localhost typically uses HTTP
                        sameSite: 'Lax'
                    };
                } else {
                    // For production, use domain for cross-subdomain sharing
                    cookieOptions = {
                        domain: '.perinhealth.com',
                        path: '/',
                        secure: true,
                        sameSite: 'Lax'
                    };
                }
                
                // Set authorization token cookie
                if (data.tokens.authorization) {
                    const authValue = data.tokens.authorization.startsWith('Bearer ') 
                        ? data.tokens.authorization 
                        : `Bearer ${data.tokens.authorization}`;
                    const domainPart = cookieOptions.domain ? `domain=${cookieOptions.domain}; ` : '';
                    const securePart = cookieOptions.secure ? 'secure; ' : '';
                    document.cookie = `authorization=${encodeURIComponent(authValue)}; ${domainPart}path=${cookieOptions.path}; ${securePart}SameSite=${cookieOptions.sameSite}`;
                }
                
                // Set refresh token cookie
                if (data.tokens.refresh) {
                    const domainPart = cookieOptions.domain ? `domain=${cookieOptions.domain}; ` : '';
                    const securePart = cookieOptions.secure ? 'secure; ' : '';
                    document.cookie = `refresh=${encodeURIComponent(data.tokens.refresh)}; ${domainPart}path=${cookieOptions.path}; ${securePart}SameSite=${cookieOptions.sameSite}`;
                }
                
                // Set user ID cookie
                if (data.tokens.id) {
                    const domainPart = cookieOptions.domain ? `domain=${cookieOptions.domain}; ` : '';
                    const securePart = cookieOptions.secure ? 'secure; ' : '';
                    document.cookie = `id=${encodeURIComponent(data.tokens.id)}; ${domainPart}path=${cookieOptions.path}; ${securePart}SameSite=${cookieOptions.sameSite}`;
                }
            }
            
            // Redirect to test app (local or production)
            const testAppOrigin =
                process.env.REACT_APP_ENVIRONMENT_STATUS === 'dev2'
                    ? 'https://testapp-dev2.perinhealth.com'
                    : 'https://testapp-dev.perinhealth.com';
            const redirectUrl = isLocalhost
                ? 'http://localhost:8001/'
                : testAppOrigin;
            window.location.href = redirectUrl;
            return;
        }
        
        // Log successful login
        Logger.shared.debug(`User ${data.user.id} logged in successfully`, null, 'Login.js');
        
        dispatch(resetAppTourState());
        dispatch(setTokens(data.tokens));
        
        // Refresh version after successful login with authentication tokens
        refreshVersion();
        
        const userData = data.user;
        
        // Update Logger with user ID for proper logging
        Logger.shared.updateAuthTokens(data.tokens);
        Logger.shared.setUserId(userData.id);  // Set the actual numeric user ID separately
        
        // Upload any pending logs that were stored before authentication
        await Logger.shared.uploadPendingLogs();
        dispatch(setPermissions(userData.role.permissions));
        dispatch(setUser(userData));
        dispatch(setUserRole(userData.role));
        if (userData.preferences) {
            dispatch(setPreferences(userData.preferences));
        }
        const islastLoginEmpty = userData.lastLogin === null;
        dispatch(setFieldValue({ name: 'showWelcome', value: islastLoginEmpty }));
        await handleGroups(userData, data.tokens);
        dispatch(closeTimeOutAlert());
        dispatch(closeAlert());
        getAllPatientMessageByUserId(userData.id);
        getAllStaffMessageByUserId(userData.id);
        refreshStaffTaskCount(axios, userData.id, dispatch);
        refreshNewReportCount(axios, userData.id, dispatch);
        refreshTodaysAppointmentsCount(axios, userData.id, dispatch);
        getDropdownLists();
        localStorage.setItem('selectedPhysicianIds', "");
        
        // Check for password policy data AFTER login is complete
        if (data.passwordPolicy) {
            const { 
                daysSinceLastChange, 
                policyDays, 
                message,
                showPopup,
                popupType,
                urgency,
                lastPasswordChange 
            } = data.passwordPolicy;

            if (showPopup && popupType) {
                // Calculate days until expiry
                const daysUntilExpiry = Math.max(0, policyDays - daysSinceLastChange);
                
                // Show password reset modal after successful login
                setTimeout(() => {
                    dispatch(showPasswordResetModal({
                        type: popupType,
                        message: message || (popupType === 'required' 
                            ? 'Password reset is required due to organization policy' 
                            : `Your password will expire in ${daysUntilExpiry} days`),
                        daysSinceLastChange: daysSinceLastChange || 0,
                        policyDays: policyDays || 0,
                        daysUntilExpiry,
                        urgency: urgency || (popupType === 'required' ? 'critical' : 'warning'),
                        lastPasswordChange
                    }));
                }, 5000); // Small delay to ensure login is complete
            }
        }

        // Navigate to landing page
        handleLandingPage(userData.role);
    };

    const getAllPatientMessageByUserId = useCallback(async (userId) => {
        try {
            const responseData = await axios.get(`chime/getAllMessageByUserId?userId=${userId}&recipientType=PATIENT`);
            const messages = responseData.data.getAllMessage || [];

            const uniquePatientIds = new Set();
            messages.forEach(message => {
                message.forEach(item => {
                    if (item.senderId !== userId && !item.readStatus) {
                        uniquePatientIds.add(item.senderId);
                    }
                });
            });

            dispatch(clearUnreadPatientMessageIds());
            uniquePatientIds.forEach(patientId => {
                dispatch(addUnreadPatientMessageId(patientId));
            });
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, []);

    const getDropdownLists = useCallback(async () => {
        try {
            const responseData = await axios.get('list/referenceCodes');
            if (responseData.data.data) {
                dispatch(setDropdowns(responseData.data.data))
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, []);

    const getAllStaffMessageByUserId = useCallback(async (userId) => {
        try {
            const responseData = await axios.get(`chime/getAllMessageByUserId?userId=${userId}&recipientType=STAFF`);
            const messages = responseData.data.getAllMessage || [];
            const uniqueStaffIds = new Set();
            messages.forEach((messageThread) => {
                const unreadInThread = messageThread.filter(
                    (message) => message.recipientId === userId && message.readStatus === false,
                );
                if (unreadInThread.length === 0) return;
                const firstUnread = unreadInThread[0];
                const senderId = firstUnread.senderId === userId ? firstUnread.recipientId : firstUnread.senderId;
                if (senderId) {
                    uniqueStaffIds.add(senderId);
                }
            });

            dispatch(clearUnreadStaffMessageIds());
            uniqueStaffIds.forEach((staffId) => {
                dispatch(addUnreadStaffMessageId(staffId));
            });
            dispatch(setNewStaffMessageCount(uniqueStaffIds.size));
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, []);

    return (
        <div className='background-container'>
            <div className='main-container'>
                {perinHealthAlert.show && (
                    <PerinHealthAlert
                        show={perinHealthAlert.show}
                        type={perinHealthAlert.type}
                        header={perinHealthAlert.header}
                        message={perinHealthAlert.message}
                        dismissable={perinHealthAlert.dismissable}
                    />
                )}
                <div className="login-container">
                    {session ? (
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '80%' }}>
                            <LoginBrandedTitle title="Enter MFA Code" variant="stacked" />
                            <p
                                className="body-2"
                                style={{
                                    textAlign: 'center',
                                    margin: '0 0 30px 0',
                                    color: '#3B3E40',
                                }}
                            >
                                {challengeParameters?.CODE_DELIVERY_DESTINATION
                                    ? `We've sent you a verification code to ${challengeParameters.CODE_DELIVERY_DESTINATION}. Please check your messages and enter the code below:`
                                    : "We've sent you a verification code. Please check your messages and enter the code below:"}
                            </p>
                            <Form.Group style={{ width: '100%' }}>
                                <FloatingLabel
                                    label="MFA Code*"
                                    className="mb-3"
                                >
                                    <Form.Control
                                        id="mfaCode"
                                        value={mfaCode}
                                        style={{ width: "100%" }}
                                        placeholder="MFA Code*"
                                        className={`${inputStyles.BorderedComponent}`}
                                        onChange={(e) => setMfaCode(e.target.value)}
                                        required
                                    />
                                </FloatingLabel>
                            </Form.Group>
                            <Button
                                className={styles.LoginButton}
                                style={{ margin: "30px 0 20px 0" }}
                                onClick={mfaFailedAttempts >= MFA_MAX_FAILED_ATTEMPTS ? handleResendMfaCode : handleMfaSubmit}
                                disabled={isMfaSubmitting || (mfaFailedAttempts < MFA_MAX_FAILED_ATTEMPTS && !mfaCode.trim())}
                            >
                                {mfaFailedAttempts >= MFA_MAX_FAILED_ATTEMPTS ? 'Resend MFA Code' : 'Submit MFA Code'}
                            </Button>
                        </div>
                    ) : (
                        <>
                            <LoginBrandedTitle title="Login" />
                            {(timeOutAlert.show || timeOutAlert.inactivityShow) && (
                                <div style={{ width: "80%" }}>
                                    <div className={inputStyles.TimeOutAlertContainer}>
                                        <span className="body-2">{timeOutAlert.inactivityShow ? "You've been signed out due to inactivity." : "You've been signed out due to privacy. Sign back in for secure access."}</span>
                                        <CloseButton onClick={() => dispatch(closeTimeOutAlert())} />
                                    </div>
                                </div>
                            )}
                            {showPatientAlert && (
                                <div style={{ width: "80%" }}>
                                    <div className={inputStyles.TimeOutAlertContainer}>
                                        <span className="body-2">Patients cannot login to the provider portal. Please download the Perin Health patient mobile app.</span>
                                        <CloseButton onClick={() => setShowPatientAlert(false)} />
                                    </div>
                                </div>
                            )}
                            <Form onSubmit={handleSubmit} noValidate validated={validated} style={{ display: "flex", flexDirection: "column", align: "left", width: "80%", flex: "1", overflow: "auto" }}>
                                <Form.Group>
                                    <FloatingLabel
                                        label="Email/Phone Number*"
                                        className="mb-3"
                                    >
                                        <Form.Control
                                            id="username"
                                            value={userLogin.username}
                                            style={{ width: "100%" }}
                                            placeholder="Email/Phone Number*"
                                            className={`${inputStyles.BorderedComponent}`}
                                            onChange={handleChange}
                                            required
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            Please enter a username.
                                        </Form.Control.Feedback>
                                    </FloatingLabel>
                                </Form.Group>
                                <Form.Group>
                                    <FloatingLabel
                                        label="Password*"
                                        className="mb-3"
                                    >
                                        <Form.Control
                                            className={`${inputStyles.BorderedComponent}`}
                                            style={{ width: "100%" }}
                                            id="password"
                                            placeholder="Password*"
                                            type={
                                                showPassword ? "text" : "password"
                                            }
                                            value={userLogin.password}
                                            onChange={handleChange}
                                            required
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            Please enter a password.
                                        </Form.Control.Feedback>
                                        <div className={inputStyles["styled-icon-container"]} onClick={() =>
                                            setShowPassword((prev) => !prev)}
                                        >
                                            {showPassword ? <FontAwesomeIcon icon="fa-regular fa-eye" className="eye-icon" size="lg" />
                                                : <FontAwesomeIcon icon="fa-regular fa-eye-slash" className="eye-icon" size="lg" />}
                                        </div>
                                    </FloatingLabel>
                                </Form.Group>
                                <div style={{ margin: "10px 0 20px 0", display: "flex", justifyContent: "space-between" }}>
                                    <label style={{ display: 'flex', color: '#3B3E40', alignItems: 'center' }}>
                                        <input
                                            id="remember_info"
                                            style={{ width: "20px", height: "20px" }}
                                            type='checkbox'
                                            checked={rememberMe}
                                            onChange={()=>setRememberMe(!rememberMe)}

                                        />
                                        <span style={{ paddingLeft: '8px' }}>
                                            Remember me
                                        </span>
                                    </label>
                                    <p style={{ color: '#1D5769', cursor: 'pointer', margin: "auto 0 auto auto" }} onClick={() => {
                                        Logger.shared.info(
                                            LogElements.forgotPasswordButton,
                                            LogAction.clicked,
                                            'Login.js'
                                        );
                                        navigate('/forgot-password');
                                    }}>
                                        Forgot Password
                                    </p>
                                </div>
                                <Button 
                                    className={styles.LoginButton}
                                    disabled={isLoading}
                                    type="submit"
                                >
                                    Login
                                </Button>
                                <a 
                                    href={getReleaseNotesUrl(axiosConfig.envURL)}
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className={styles.versionNumber}
                                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                >
                                    {`Provider Portal v${version}`}
                                </a>
                            </Form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Login;
